/**
 * The shared strategic-analysis screen.
 *
 * One route, both personas. The reports are versioned company artifacts rather
 * than per-viewer documents, so a founder and a permitted investor read the same
 * thing — they differ only in what they may DO to it.
 *
 * `startupId` SELECTS DATA AND GRANTS NOTHING. Persona comes from
 * `useUserRole`, and edit authority from `useMyStartup`, which resolves active
 * `team_members` membership server-side. A route parameter, a `canEdit` query
 * string, or a startup object handed in by a caller cannot make someone a member
 * — every one of those is attacker-controlled.
 *
 * NOT A TAB, BUT MOUNTED IN TWO PLACES. Neither persona's tab bar gains a
 * destination — both mount points are `href: null`/root-Stack — but a founder
 * reaches this screen from inside the `(startup)` navigator and an investor from
 * `startup-detail`, which is not in that group. Pushing a founder to the root
 * route unmounted their tab bar mid-session, so `(startup)/strategic-analysis`
 * re-exports this screen and the root route serves the investor. On web both sit
 * inside `AppLayout` so the persona's sidebar stays put.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, Text, View } from 'react-native';
import { Stack, usePathname, useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AlertCircle, ChevronLeft } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useAuth } from '@/lib/supabase/auth';
import { useUserRole } from '@/lib/auth/role';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { useFlag } from '@/lib/feature-flags';
import { isMockModeActive } from '@/lib/state/mock-data-provider';
import { ANALYSIS_LABELS, type AnalysisType, type ClaimView } from '@/lib/strategic-analysis/types';
import { parseAnalysisTab } from '@/lib/strategic-analysis/route';
import { isGenerateOffered, type ReportViewState } from '@/lib/strategic-analysis/report-state';
import { REPORT_SCHEMA_VERSION, type ReadyReport } from '@/lib/strategic-analysis/report-contract';
import type { ResolvedRunState } from '@/lib/strategic-analysis/status';
import { messageFor, safeFailureCodeOf } from '@/lib/strategic-analysis/failure-messages';
import { useAiConsentGate } from '@/lib/consent/ai-consent';
import { AnalysisProblemCard } from '@/components/strategic-analysis/AnalysisProblemCard';
import { LegacyAnalysisBody } from '@/components/strategic-analysis/LegacyAnalysisBody';
import { planBulkRefresh } from '@/lib/strategic-analysis/bulk-refresh';
import {
  useBoundExitThesis,
  useBoundMarketAssumptions,
  useInputFingerprints,
  useLatestExitThesis,
  useLatestMarketAssumptions,
  useMethodologyManifest,
  useRunEvidence,
  useSaveExitThesis,
  useSaveMarketAssumptions,
  useSaveStrategyContext,
  useStartupName,
  useRefreshAllStrategicAnalyses,
  useStrategicAnalysis,
  useStrategyContextDraft,
} from '@/lib/strategic-analysis/use-strategic-analysis';
import {
  MOCK_ASSUMPTIONS,
  MOCK_CLAIMS,
  MOCK_COMPETITION,
  MOCK_STARTUP_NAME,
  MOCK_COVERAGE,
  MOCK_DERIVATION,
  MOCK_EXIT,
  MOCK_EXIT_THESIS,
  MOCK_MARKET,
  MOCK_PESTEL,
  MOCK_PORTER,
  MOCK_SNAPSHOT,
  MOCK_SOURCES,
} from '@/lib/strategic-analysis/mock-data';
import {
  AnalysisSetupModal,
  type SetupTarget,
} from '@/components/strategic-analysis/AnalysisSetupModal';
import { AnalysisWidthBoundary } from '@/components/strategic-analysis/AnalysisPrimitives';
import { AnalysisShell } from '@/components/strategic-analysis/AnalysisShell';
import { AnalysisTabs } from '@/components/strategic-analysis/AnalysisTabs';
import { AnalysisBody } from '@/components/strategic-analysis/AnalysisBody';
import type {
  ContextSelection,
  FounderInput,
} from '@/components/strategic-analysis/StrategyContextEditor';
import { CompetitionAnalysis } from '@/components/strategic-analysis/CompetitionAnalysis';
import { PorterFiveForces } from '@/components/strategic-analysis/PorterFiveForces';
import { PestelAnalysis } from '@/components/strategic-analysis/PestelAnalysis';
import { MarketValidation } from '@/components/strategic-analysis/MarketValidation';
import { ExitStrategyAnalysis } from '@/components/strategic-analysis/ExitStrategyAnalysis';

export default function StrategicAnalysisScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const params = useLocalSearchParams<{ startupId?: string; tab?: string; setup?: string }>();
  const { user } = useAuth();

  const startupId = typeof params.startupId === 'string' ? params.startupId : undefined;
  const [tab, setTab] = useState<AnalysisType>(() => parseAnalysisTab(params.tab));

  // THE URL AND THE TAB TRACK EACH OTHER, IN BOTH DIRECTIONS.
  //
  // URL → tab: `useState` initialises ONCE, and Expo Router reuses this screen
  // when only the query string changes, so a later `?tab=` — a deep link from
  // the entry card, a shared URL, a back/forward step — was read and thrown
  // away. The URL said one analysis while the screen showed another, and any
  // action taken then (Generate, Refresh) ran against the analysis the screen
  // still believed it was on. Found by generating from a `?tab=pestel` URL and
  // getting a porter run back.
  //
  // tab → URL: pressing a tab only moved local state, so the address bar kept
  // naming whichever analysis the screen opened on, and nothing the reader was
  // looking at was linkable. Back/Forward then work for free — a history step
  // changes the query, which lands here as a new `routeTab`.
  const routeTab = parseAnalysisTab(params.tab);
  useEffect(() => {
    // Same-value `setState` bails out, so this cannot loop against `selectTab`
    // writing the URL that produced this `routeTab`.
    setTab((current) => (current === routeTab ? current : routeTab));
  }, [routeTab]);

  //
  // THE TWO PLATFORMS NEED DIFFERENT VERBS, and using one for both is what the
  // last attempt got wrong. `setParams` dispatches React Navigation's in-place
  // param action (expo-router 6.0.23, `global-state/routing.js`), so the browser
  // entry is REPLACED: the URL was shareable and reloadable, which was the point,
  // but Competition → Porter → PESTEL left one entry behind it and Back skipped
  // straight out of the screen. Only `linkTo(..., { event: 'PUSH' })` — what
  // `router.push` calls — adds an entry.
  //
  // `push` cannot be used on native, though. This route is registered on the
  // root Stack, and routing.js keeps PUSH as PUSH when the navigator is a stack
  // (it downgrades to NAVIGATE only for non-stacks), so every tab press would
  // mount ANOTHER strategic-analysis screen and the back button would walk
  // through duplicates of the screen you never left. Native keeps `setParams`,
  // which is the correct behaviour there: one screen, its params edited.
  const pathname = usePathname();
  const selectTab = useCallback(
    (next: AnalysisType) => {
      // Pressing the tab you are already on is not navigation. Without this
      // guard web pushes an identical entry, so Back lands on the same screen
      // and appears to do nothing.
      if (next === tab) return;
      // Optimistic, so the press is not waiting on a round trip through the
      // router. The effect above then agrees rather than fighting it — a
      // same-value `setState` bails out, so there is no second render and no
      // flicker between the press and the URL catching up.
      setTab(next);
      if (isWeb) {
        // EVERY current param is carried, not a chosen subset. Spreading
        // `params` is what keeps `startupId` — and anything added to this route
        // later that nobody remembers to add here — instead of rebuilding the
        // query from the fields this screen happens to read today.
        router.push({ pathname, params: { ...params, tab: next } });
        return;
      }
      router.setParams({ tab: next });
    },
    [tab, router, pathname, params],
  );

  const { data: role } = useUserRole(user?.id);
  const { startupId: myStartupId } = useMyStartup();

  // Edit authority is membership OF THIS STARTUP, resolved server-side. Not the
  // route, not the persona, and not "they got here somehow".
  const canEdit = Boolean(startupId && myStartupId && myStartupId === startupId);
  const isFounderView = canEdit || role === 'startup';

  const mock = isMockModeActive();

  // In sample-data mode this screen is a self-contained demo: nothing below
  // reaches Supabase, a search provider or a model. Gating the demo on a remote
  // flag that resolves to its default in exactly that mode would leave it
  // permanently unreachable — the flag still governs every real report.
  const flagEnabled = useFlag('strategic_analyses', { default: false }) || mock;

  // AI-PROCESSING CONSENT — gated at SCREEN ENTRY, not only at Generate.
  //
  // The server checks consent before EVERY startup-scoped action this screen
  // can trigger — draft, fingerprints, save*, generate (strategic-analysis
  // handler.ts step 6) — not only `generate`. Letting `fingerprints`/`draft`
  // fire while unconsented just 403s them silently: `approvedContextVersionId`
  // (read off `draft.data`) never resolves, Generate is never even OFFERED,
  // and the generate-time consent-required recovery below never gets a
  // chance to run. Gating those two queries' `enabled` on `consented`, and
  // showing the sheet before they would otherwise fire, is what actually
  // reaches the point where consent CAN be granted.
  //
  // FOUNDER-ONLY: every consent-gated action is already behind `canEdit` —
  // an investor's report reads are plain RLS selects through
  // run-queries.ts, never through this function, so an investor's own
  // (never-asked) consent state must never hold those back.
  const analysisConsentGate = useAiConsentGate(['strategic_analysis']);
  const needsStrategicConsent = canEdit && !mock && flagEnabled;
  const strategicConsented = analysisConsentGate.consented;
  useEffect(() => {
    // `!analysisConsentGate.isLoading` matters: the query starts `false` on
    // every mount, so deciding before it resolves would flash the sheet at
    // an already-consented founder on every visit.
    //
    // NON-recovery open (`runWithConsent`, not `openConsentSheet`): this is
    // the first time the screen has asked, not a server 403. `openConsentSheet`
    // sets recovery semantics (`fresh: true` on accept) — reserved for the
    // Generate-time 403 recovery below. Using it here meant one flaky read
    // could open the sheet, a `fresh` re-read find nothing missing, and
    // `ConsentVerificationError` surface behind a screen that had already
    // unblocked. The action is a no-op: the screen's own queries already
    // react to `strategicConsented` once granted.
    if (needsStrategicConsent && !analysisConsentGate.isLoading && !strategicConsented) {
      analysisConsentGate.runWithConsent(() => {});
    }
  }, [needsStrategicConsent, analysisConsentGate.isLoading, strategicConsented, analysisConsentGate.runWithConsent]);

  // What today's inputs WOULD fingerprint to. Without it `isStale` is false for
  // every run, so a report built from superseded inputs reads as current and the
  // refresh path is unreachable. Founder-only: the action sits behind the
  // membership gate — and now behind consent too, for the same reason.
  const fingerprints = useInputFingerprints(startupId, {
    enabled: flagEnabled && !mock && canEdit && strategicConsented,
  });

  // FRESHNESS UNKNOWN IS NOT FRESHNESS CONFIRMED. `isStale` is computed by
  // comparing today's fingerprint to the one stored on the run, so a failed
  // fingerprint read makes every report look current — the exact reading a
  // founder should not be given when the thing that decides it did not load.
  // Passing `undefined` already suppresses the stale badge; the banner below is
  // what stops that silence reading as confirmation.
  const freshnessUnknown = Boolean(fingerprints.error) && canEdit && !mock;

  const run = useStrategicAnalysis(startupId, tab, {
    enabled: flagEnabled && !mock,
    currentInputFingerprint: fingerprints.data?.[tab],
  });

  // Evidence hangs off the run whose report is actually DISPLAYED, which during
  // a refresh is the previous one — asking for the in-flight run's evidence
  // would strip the citations off the report on screen.
  const evidence = useRunEvidence(startupId, run.data?.displayedRunId);
  // CITATIONS ARE A SEPARATE READ, and it can fail on its own. A failure leaves
  // every "View sources" control absent, which is indistinguishable from a
  // report whose claims were all rejected — so it is named rather than rendered
  // as the same silence. (In flight, the shell's own source count is simply
  // omitted rather than shown as zero; see `provenanceLine`.)
  const evidenceFailed = Boolean(run.data?.displayedRunId) && Boolean(evidence.error);

  // The BOUND versions this report read, resolved from its own snapshot. Two of
  // the five bodies need them; the rest ignore them, so neither is fetched on a
  // tab that would not use it.
  const bound = run.data?.boundVersionIds;
  const boundAssumptions = useBoundMarketAssumptions(
    startupId,
    tab === 'market_validation' ? bound?.marketAssumptionVersionId : null,
  );
  const boundThesis = useBoundExitThesis(
    startupId,
    tab === 'exit_strategy' ? bound?.exitThesisVersionId : null,
  );

  // The company being analysed, by name. Read from the profile row so RLS
  // decides whether the viewer may see it — never taken from the route, which
  // would let a caller caption someone else's report with a chosen string.
  // A FAILED READ IS NOT AN ABSENT VERSION. Both bodies say "No founder
  // assumptions were bound" / "No thesis was assessed" when they receive null,
  // which is a statement about the report. A query that failed says nothing
  // about the report at all.
  const boundInputFailed = Boolean(
    (tab === 'market_validation' && boundAssumptions.error) ||
      (tab === 'exit_strategy' && boundThesis.error),
  );

  const startupName = useStartupName(startupId);
  const displayName = mock ? MOCK_STARTUP_NAME : (startupName.data ?? null);

  // Generation needs an approved setup version to run FROM. The draft is the
  // founder-only call that knows the latest one, so this is gated on
  // membership (and consent — see `strategicConsented` above) and stays
  // untouched for an investor.
  const draft = useStrategyContextDraft(startupId, {
    enabled: flagEnabled && !mock && canEdit && strategicConsented,
  });
  const approvedContextVersionId = draft.data?.draft.previousApprovedVersion?.id;
  // WHICH BOUND VERSION THIS TAB REQUIRES, and whether the server has one.
  //
  // Competition, Porter and PESTEL bind only the strategy context. Market
  // Validation must bind an exact market-assumption version and Exit Strategy an
  // exact thesis version — the run row's own CHECK constraints require it, the
  // readiness evaluator requires it, and the report states which version it
  // assessed. The screen used to send `strategyContextVersionId` alone, so both
  // of those analyses were refused on every attempt with nothing on screen
  // explaining why.
  const boundVersions = draft.data?.boundVersions;
  const requiredBoundVersionId = tab === 'market_validation'
    ? boundVersions?.marketAssumptionVersionId ?? null
    : tab === 'exit_strategy'
    ? boundVersions?.exitThesisVersionId ?? null
    : null;
  const boundVersionMissing = (tab === 'market_validation' || tab === 'exit_strategy') &&
    !requiredBoundVersionId;

  // REFRESHING ALL FIVE, which is how a founder thinks about it. One strategy
  // context produces five reports, so a founder who has just changed that
  // context wants all five to catch up rather than visiting five tabs. The plan
  // is computed from exactly the inputs a single tab uses, so "Refresh all"
  // cannot start something the tab itself would refuse.
  const refreshAll = useRefreshAllStrategicAnalyses(startupId ?? '');
  const refreshPlan = useMemo(
    () => planBulkRefresh({
      approvedContextVersionId: approvedContextVersionId ?? null,
      readiness: draft.data?.draft.readiness,
      boundVersions: boundVersions ?? undefined,
    }),
    [approvedContextVersionId, draft.data, boundVersions],
  );
  /**
   * REFRESHING REFRESHES EVERYTHING IT CAN. One strategy context produces five
   * reports, so "refresh" means the set, not the tab you happen to be on — a
   * founder should not have to visit five tabs to bring their company up to
   * date. Analyses that cannot run are simply not started; the tab for each one
   * already says why, and a second surface listing them was clutter.
   */
  const startRefreshAll = () => {
    refreshAll.reset();
    refreshAll.mutate(
      refreshPlan
        .filter((entry) => entry.willRun)
        .map((entry) => ({
          analysisType: entry.analysisType,
          strategyContextVersionId: approvedContextVersionId as string,
          boundVersionId: entry.willRun ? entry.boundVersionId : null,
        })),
    );
  };

  const canRequest = canEdit && Boolean(approvedContextVersionId) && !boundVersionMissing &&
    !refreshAll.isPending;

  // WHICH ANALYSIS THE FAILURE BELONGS TO.
  //
  // One press starts every analysis that can run, so a refusal has to be
  // attributed rather than shown wherever the reader happens to be standing — a
  // refused Porter run must not put a banner over PESTEL. The fan-out records an
  // outcome per analysis, so the attribution is read from the result rather than
  // tracked in a separate piece of state.
  const generationErrorForTab = refreshAll.data?.find(
    (result) => result.analysisType === tab && !result.started,
  )?.error ?? null;
  const generationErrorIsConsentRequired = generationErrorForTab
    ? safeFailureCodeOf(generationErrorForTab) === 'consent_required'
    : false;

  // 403 recovery for Generate specifically (`analysisConsentGate` is declared
  // near the top of the component, alongside the screen-entry gate — one
  // gate, one sheet, for the whole screen). `generationErrorForTab`'s
  // `consent_required` code is the server's answer, read back from the SAME
  // bulk-refresh result the reader already sees per tab — reopen the sheet
  // instead of a dead-end message (`FAILURE_MESSAGES.consent_required`,
  // which names no way to fix it).
  useEffect(() => {
    if (generationErrorIsConsentRequired) analysisConsentGate.openConsentSheet();
  }, [generationErrorIsConsentRequired, analysisConsentGate.openConsentSheet]);

  // ONE ACTION, EVERY ANALYSIS IT CAN START. Refresh used to mean "this tab",
  // so bringing a company up to date after a context change meant visiting five
  // of them. It now means the set — the analyses that cannot run are simply not
  // started, and each one's own tab already says why.
  const requestAnalysis = () => analysisConsentGate.runWithConsent(startRefreshAll);

  // ---- analysis setup ------------------------------------------------------
  //
  // Setup lives in a MODAL, over the analysis. It used to be inserted into the
  // report scroll above the analysis, so answering a blocked field pushed the
  // thing you came to read off the screen.
  //
  // Opened by the route (`?setup=1`, which is where a blocked entry card and the
  // dashboard both point) or by the reminder button. Local state, so answering a
  // gap on THIS tab needs no navigation round-trip that would lose the tab.
  const saveContext = useSaveStrategyContext(startupId ?? '');
  const readiness = draft.data?.draft.readiness?.[tab] ?? null;
  const [setupTarget, setSetupTarget] = useState<SetupTarget | null>(
    params.setup === '1' ? 'setup' : null,
  );
  // EDIT AUTHORITY IS THE GATE. `canEdit` is server-backed active membership,
  // resolved from `useMyStartup` — never a route parameter. An investor reading
  // someone else's company gets `null` here, so no editor is ever constructed
  // for them and no edit control is rendered to press. The edge function and RLS
  // refuse the write independently; this only decides what is on screen.
  //
  // `strategicConsented` too — `save_strategy_context` / `save_market_assumptions`
  // / `save_exit_thesis` are consent-gated same as `draft`, and a `?setup=1`
  // deep link must not open THIS modal while the consent sheet also wants to
  // be open: two AppModals must never stack.
  const openTarget = canEdit && !mock && strategicConsented ? setupTarget : null;

  // BLOCKED ANALYSES ASK, THEY DO NOT JUST REFUSE. The server rejects generation
  // for any readiness that is not `ready`, and this screen used to say nothing
  // about it: an approved setup existed, so no form was shown, and the shell
  // offered a Generate button whose only possible outcome was a refusal. A
  // founder on Market Validation with no bound market assumptions had no way to
  // learn that from this screen. One reminder button now says so and opens the
  // form over the page.
  //
  // Founder-only, like everything else derived from the draft — readiness
  // describes private material about a company an investor is only reading.
  // A missing bound version blocks exactly as a missing context field does, and
  // opens the same form. Without this the founder saw an enabled Generate button
  // whose only possible outcome was a server refusal.
  const blockedHere = canEdit && !mock &&
    ((!!readiness && readiness.status !== 'ready') || boundVersionMissing);

  // Which of this analysis's inputs live on a BOUND VERSION rather than on the
  // strategy context. They are satisfied by binding a version — a context field
  // of the same name does not count, which is the rule the server applies.
  const manifest = useMethodologyManifest({ enabled: flagEnabled && !mock });
  // WHICH FIELDS LIVE ON A BOUND VERSION. Without the manifest this set is
  // empty, and an empty set sends every blocking field to the CONTEXT editor —
  // including `customerUnitsAssumption`, which that editor will happily collect
  // and write to a context field the readiness check never reads. The founder
  // then answers the question and stays blocked, with no way to tell why.
  //
  // So a failed or pending manifest suppresses the focused form rather than
  // opening the wrong one.
  const manifestUnavailable = Boolean(manifest.error) || manifest.isLoading;
  const boundVersionInputs = useMemo(
    () =>
      new Set(
        manifest.data?.manifest.analyses.find((a) => a.analysisType === tab)?.boundVersionInputs ?? [],
      ),
    [manifest.data, tab],
  );

  // Focused mode asks only what is blocking THIS analysis. A founder sent here
  // by a blocked card should answer the gap, not re-read a form they filled in.
  //
  // BOUND-VERSION FIELDS ARE EXCLUDED, and that exclusion is load-bearing. The
  // context editor renders a focus field whether or not any candidate was found
  // for it, so `customerUnitsAssumption` would appear as a free-text question a
  // founder could confidently answer — and saving it writes a context field the
  // readiness check never reads, leaving them blocked with no idea why. Those
  // fields are captured by their own editor in the modal.
  const blockingFields = readiness && readiness.status !== 'ready' && !manifestUnavailable
    ? [
      ...readiness.missingRequired,
      ...readiness.unconfirmedRequired,
      ...readiness.conflicts.map((c) => c.fieldKey),
    ].filter((key) => !boundVersionInputs.has(key))
    : [];

  const saveSetup = (
    selections: ContextSelection[],
    founderInputs: FounderInput[],
  ) => {
    const fingerprint = draft.data?.draft.draftFingerprint;
    // The fingerprint is what the server validates staleness against. Saving
    // without one would be rejected, so the button simply does nothing rather
    // than reporting a failure the founder cannot act on.
    if (!fingerprint) return;
    // Closing on SUCCESS is the confirmation. The draft refetches, readiness
    // moves, and the reminder button disappears on the screen the founder is
    // returned to — which answers "did that work?" better than a toast.
    saveContext.mutate(
      { draftFingerprint: fingerprint, selections, founderInputs },
      { onSuccess: closeSetup },
    );
  };

  // The two analyses whose inputs are a bound VERSION: market assumptions and an
  // exit thesis. Both editors existed, fully built and tested, and neither was
  // reachable from anywhere in the app — which is why both analyses refused on
  // every real run. A founder could not have supplied what they were missing.
  const saveAssumptions = useSaveMarketAssumptions(startupId ?? '');
  const saveThesis = useSaveExitThesis(startupId ?? '');

  // THE LATEST VERSIONS, WHICH ARE WHAT AN EDIT STARTS FROM — deliberately not
  // the ones the report bound. A published report is a statement about the exact
  // inputs it read, and it keeps showing those; seeding the editor from them
  // would make every revision look like an edit to history. Founder-only:
  // `canEdit` gates the fetch, and RLS refuses it regardless.
  const latestAssumptions = useLatestMarketAssumptions(startupId, {
    enabled: flagEnabled && !mock && canEdit && tab === 'market_validation',
  });
  const latestThesis = useLatestExitThesis(startupId, {
    enabled: flagEnabled && !mock && canEdit && tab === 'exit_strategy',
  });

  // The failure belonging to whatever is open, so a stale error from a different
  // form never appears under this one.
  const saveError = setupTarget === 'assumptions'
    ? saveAssumptions.error
    : setupTarget === 'thesis'
    ? saveThesis.error
    : saveContext.error;

  // ONE WAY IN AND ONE WAY OUT, both of which clear every save failure.
  //
  // A mutation holds its error until something resets it, and these three
  // outlive the modal. Without this, a failed assumptions save stayed on the
  // hook: reopening showed "Nothing was saved" above an untouched form, and
  // opening the THESIS editor showed the assumptions failure under it — an
  // error about a document that was not on screen. The error still persists for
  // as long as its own session is open, which is what makes it readable.
  const openSetup = useCallback(
    (target: SetupTarget) => {
      saveContext.reset();
      saveAssumptions.reset();
      saveThesis.reset();
      setSetupTarget(target);
    },
    [saveContext, saveAssumptions, saveThesis],
  );

  const closeSetup = useCallback(() => {
    setSetupTarget(null);
    saveContext.reset();
    saveAssumptions.reset();
    saveThesis.reset();
  }, [saveContext, saveAssumptions, saveThesis]);
  // Blocked, and every blocker is on a bound version. The context editor has
  // nothing to ask in that case, and showing it empty above the editor that DOES
  // hold the answer reads as two forms with one of them broken. A founder who
  // opened setup with nothing blocking still gets the full form — editing your
  // inputs is not the same act as unblocking an analysis.
  const onlyBoundBlockers = blockedHere && blockingFields.length === 0;
  const showContextEditor = !onlyBoundBlockers;

  // The reminder is also the only way in when nothing has been approved yet:
  // without an approved version `canRequest` is false, so the shell offers no
  // control either and the founder would have no move on this screen at all.
  // A FAILED DRAFT READ IS NOT "NOTHING HAS BEEN APPROVED". The reminder button
  // is derived from the absence of an approved version, and a query that failed
  // produces the same absence — so a founder with a complete setup was told to
  // complete it, and the analysis they had already generated was hidden behind
  // a block that did not exist.
  const draftFailed = canEdit && !mock && Boolean(draft.error);
  const needsSetup = canEdit && !mock && !draft.isLoading && !draftFailed &&
    (blockedHere || !approvedContextVersionId);

  // ONE VALUE FOR WHAT IS ON SCREEN. Everything the shell and the bodies render
  // comes from here, and the raw run row is no longer read directly by either.
  const view: ReportViewState = mock
    ? {
      kind: 'ready',
      report: mockReadyReport(tab),
      displayedRunId: 'mock-run',
      schemaVersion: REPORT_SCHEMA_VERSION,
      showingPrevious: false,
      isStale: false,
    }
    : run.isLoading
    ? { kind: 'loading' }
    : run.error
    ? { kind: 'read_error', error: run.error }
    : run.data?.view ??
      (blockedHere
        ? { kind: 'missing_inputs' }
        : generationErrorForTab
        ? { kind: 'generation_error', error: generationErrorForTab }
        : { kind: 'not_generated' });

  // The status card speaks the run vocabulary; the three contract problems have
  // no run state of their own and must not be rendered as `not_generated`.
  //
  // THIS COMMENT DESCRIBED AN INTENTION THE CODE DID NOT KEEP. The chain below
  // had no branch for them, so `legacy_report`, `unsupported_version` and
  // `malformed` fell through to the default and printed "Not generated yet /
  // Run it when your analysis inputs are ready" directly under the card
  // explaining the report predates the evidence contract — on a screen whose
  // header read "Updated 17 Aug 2026 · 65 sources". `null` now means the
  // problem card owns the screen, and it carries the action.
  const shellState: ResolvedRunState | null = mock
    ? 'ready'
    : view.kind === 'legacy_report' ||
      view.kind === 'unsupported_version' ||
      view.kind === 'malformed'
    ? null
    : view.kind === 'ready'
    ? 'ready'
    : view.kind === 'in_progress'
    ? view.runState
    : view.kind === 'failed'
    ? 'failed'
    : view.kind === 'timed_out'
    ? 'timed_out'
    : view.kind === 'insufficient_evidence'
    ? 'insufficient_evidence'
    : view.kind === 'loading'
    // A spinner is not an absence. The status card reads `queued` as "work is
    // happening", which is the honest thing to show while the query resolves.
    ? 'queued'
    : 'not_generated';

  const back = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    // A deterministic, persona-appropriate landing when there is no history —
    // a deep link opened cold has none.
    router.replace(isFounderView ? '/(startup)/company' : `/startup-detail?id=${startupId ?? ''}`);
  };

  const breadcrumbs = useMemo(
    () => [
      isFounderView
        ? { label: displayName ?? 'Company Profile', href: '/(startup)/company' as never }
        : { label: displayName ?? 'Startup Detail', href: `/startup-detail?id=${startupId ?? ''}` as never },
      { label: 'Strategic Analysis' },
      // Which analysis, not just which screen — five tabs behind one crumb
      // left a reader with several open reports unable to tell them apart.
      { label: ANALYSIS_LABELS[tab] },
    ],
    [isFounderView, startupId, displayName, tab],
  );

  // Anchored above the scroll area, so it stays put while the report moves and
  // reads as chrome rather than as the first card of the page.
  const tabs = flagEnabled && startupId ? <AnalysisTabs value={tab} onChange={selectTab} /> : null;

  const content = (
    <ScrollView contentContainerStyle={{ padding: 20 }} showsVerticalScrollIndicator={false}>
      {/* Measures the REAL content column — inside the shell's sidebar and this
          padding — and publishes it to every body's layout decisions. The gap
          moves here with it, since this View is now what holds the children. */}
      <AnalysisWidthBoundary style={{ gap: 20 }}>
      {!flagEnabled
        ? (
          <View className="rounded-2xl p-4" style={{ backgroundColor: colors.surface.secondary }}>
            <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
              Strategic analysis is not available for this account yet.
            </Text>
          </View>
        )
        : !startupId
        ? (
          <View className="rounded-2xl p-4" style={{ backgroundColor: colors.surface.secondary }}>
            <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
              No company was selected.
            </Text>
          </View>
        )
        : needsStrategicConsent && analysisConsentGate.isLoading
        ? (
          // WHILE THE CONSENT READ ITSELF IS IN FLIGHT, `strategicConsented`
          // is `false` for the same reason `useAiConsent.consented` always
          // starts `false` on mount — it does not yet mean "not consented".
          // Without this guard an already-consented founder saw the "consent
          // needed" card flash before the report on every visit.
          <View className="items-center py-10">
            <ActivityIndicator size="small" color={colors.primary[600]} />
          </View>
        )
        : needsStrategicConsent && !strategicConsented
        ? (
          // A CALM STATE, NOT AN ERROR. Every query this screen needs is
          // disabled until consent is granted (see `strategicConsented`
          // above), so there is genuinely nothing to report yet — this says
          // why, rather than falling through to whatever the disabled
          // queries' absence of data would otherwise render (a blank
          // "no analysis" state that read as a bug, not a decision).
          <View
            className="rounded-2xl p-5"
            style={{ backgroundColor: colors.surface.secondary, gap: 12 }}
          >
            <Text className="text-base font-semibold" style={{ color: colors.text.primary }}>
              AI processing consent needed
            </Text>
            <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
              Strategic analysis uses AI and external research to build competitive, market,
              industry-force, PESTEL, and exit-strategy reports for your company. Allow AI
              processing to continue.
            </Text>
            <AnimatedPressable
              // NON-recovery open, same reasoning as the screen-entry effect
              // above: this button only renders from the confirmed,
              // non-flaky `needsStrategicConsent && !strategicConsented`
              // state, never from a server 403 — `openConsentSheet` here
              // risked the identical `fresh: true` false-negative
              // (ConsentVerificationError behind an already-unblocked
              // screen) that the screen-entry fix closed.
              onPress={() => analysisConsentGate.runWithConsent(() => {})}
              accessibilityRole="button"
              accessibilityLabel="Allow AI processing for strategic analysis"
              className="rounded-xl px-4 py-3 self-start"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Text className="text-sm font-semibold" style={{ color: colors.text.inverse }}>
                Allow AI processing
              </Text>
            </AnimatedPressable>
          </View>
        )
        : (
          <>
            {/* A REMINDER, NOT A REPORT. This was a red panel listing every
                missing and optional field, plus a question for each — which
                restated the form directly above the form, and made "something
                is missing" look like a page of bad news. The button in the
                warning colour carries the whole message; what is missing is
                the form's job to ask, once, in the place you answer it. */}
            {needsSetup && (
              <SetupReminderButton
                label={ANALYSIS_LABELS[tab]}
                onPress={() => openSetup('setup')}
              />
            )}

            {/* THE STATES THAT ARE NOT A REPORT AND NOT AN ABSENCE.
                A read failure, a refused generation, a report newer than this
                build and a body that fails its own contract all used to render
                as "no analysis has been generated", with a Generate button
                under them — which spends a monthly run to reproduce a report
                that already exists. */}
            {view.kind === 'read_error' && (
              <AnalysisProblemCard
                kind="read_error"
                message={messageFor(safeFailureCodeOf(view.error))}
                onRetry={() => { void run.refetch(); }}
              />
            )}
            {/* SHOWN BESIDE THE REPORT, never instead of it. A refused refresh
                does not un-publish what is already on screen — and the banner is
                scoped to the analysis that was requested, so switching tabs does
                not carry another tab's failure with it. */}
            {generationErrorForTab && !generationErrorIsConsentRequired && (
              <AnalysisProblemCard
                kind="generation_error"
                message={messageFor(safeFailureCodeOf(generationErrorForTab))}
                onRetry={canRequest ? requestAnalysis : undefined}
              />
            )}
            {view.kind === 'unsupported_version' && <AnalysisProblemCard kind="unsupported_version" />}
            {view.kind === 'malformed' && <AnalysisProblemCard kind="malformed" />}
            {/* A well-formed report from an OLDER contract. Refreshing is the
                fix and it is offered, because a new run produces a report under
                the current evidence contract. Rendering the old body through
                today's components would show it with no citations anywhere and
                no speculation labels, which is worse than declining to draw it. */}
            {/* It carries the action itself, because suppressing the status card
                is what removed the button. `isGenerateOffered` already says
                legacy_report takes one — a new run publishes under the current
                contract — so naming the problem without offering the fix would
                be a worse screen than the contradictory one this replaced. The
                other two problem kinds stay actionless on purpose: regenerating
                cannot repair a corrupt body or a version this build cannot read. */}
            {view.kind === 'legacy_report' && (
              <>
                <AnalysisProblemCard
                  kind="legacy_report"
                  onRetry={canRequest ? requestAnalysis : undefined}
                  retryLabel="Refresh analysis"
                />
                {/* THE FINDINGS THEMSELVES, from the older contract. Refusing to
                    draw them was half right: the provenance must not be
                    invented, but the analysis WAS published, and hiding it left
                    a founder with five reports looking at an empty screen. This
                    reader offers no citation affordance at all — there is
                    nothing to cite with, and a disabled control would imply
                    otherwise. */}
                <LegacyAnalysisBody report={view.report} />
              </>
            )}

            {/* THE READS THAT ARE NOT THE REPORT. Each of these used to fail
                silently into a sentence about the analysis — no sources, no
                assumptions bound, nothing approved, everything current — which
                are statements about a company made on the strength of a query
                that did not return. */}
            {evidenceFailed && (
              <AnalysisProblemCard
                kind="read_error"
                message="The sources behind this analysis could not be loaded, so citations are unavailable. The findings themselves are unaffected."
                onRetry={() => { void evidence.refetch(); }}
              />
            )}
            {boundInputFailed && (
              <AnalysisProblemCard
                kind="read_error"
                message="The founder inputs this analysis was built from could not be loaded. They are not missing — this view simply could not read them."
              />
            )}
            {draftFailed && (
              <AnalysisProblemCard
                kind="read_error"
                message="Your analysis setup could not be loaded, so editing is unavailable right now. Nothing has been lost."
                onRetry={() => { void draft.refetch(); }}
              />
            )}
            {freshnessUnknown && (
              <AnalysisProblemCard
                kind="read_error"
                message="Whether this report is still current could not be checked. Treat its freshness as unknown rather than confirmed."
                onRetry={() => { void fingerprints.refetch(); }}
              />
            )}

            <AnalysisShell
              analysisType={tab}
              state={shellState}
              failureCode={mock ? null : run.data?.failureCode}
              reportShown={view.kind === 'ready'}
              lastPublishedAt={mock ? null : run.data?.lastPublishedAt ?? null}
              isStale={view.kind === 'ready' && view.isStale}
              showingPrevious={view.kind === 'ready' && view.showingPrevious}
              canGenerate={canEdit}
              // Presence is what renders the control, so both stay undefined
              // until there is an approved setup version to generate FROM — and
              // while this analysis is blocked, because the server refuses any
              // readiness that is not `ready` and a button whose only outcome is
              // a refusal is worse than no button. The readiness card above
              // carries the action that actually moves the founder forward.
              onGenerate={canRequest && !blockedHere && isGenerateOffered(view) ? requestAnalysis : undefined}
              onRefresh={canRequest && !blockedHere && isGenerateOffered(view) ? requestAnalysis : undefined}
              coverage={mock ? MOCK_COVERAGE : (run.data?.coverage ?? null)}
              derivation={mock ? MOCK_DERIVATION : (run.data?.derivation ?? null)}
              snapshot={mock ? MOCK_SNAPSHOT : (run.data?.snapshot ?? null)}
              sources={mock ? MOCK_SOURCES : (evidence.data?.sources ?? [])}
            >
              {(inspectClaim) => (mock
                ? <MockBody tab={tab} onInspectClaim={inspectClaim} />
                : run.data?.previousLegacyReport
                // THE ANALYSIS THE FOUNDER STILL HAS, in the slot a report body
                // occupies — AFTER the run's outcome, not above it. Rendered
                // above, it read as the thing "Not enough evidence to publish"
                // was describing, which is a statement about the NEW run and not
                // about the published analysis being shown.
                ? <LegacyAnalysisBody report={run.data.previousLegacyReport} />
                : view.kind === 'ready'
                ? (
                  <AnalysisBody
                    report={view.report}
                    claims={evidence.data?.claims ?? []}
                    assumptions={boundAssumptions.data ?? null}
                    assessedThesis={boundThesis.data ?? null}
                    startupName={displayName ?? undefined}
                    // The wire that was missing: without it no body ever
                    // renders "View sources" and the evidence modal the shell
                    // owns can never be opened.
                    onInspectClaim={inspectClaim}
                    // FOUNDER-ONLY, by construction. `canEdit` is server-backed
                    // active membership; an investor gets `undefined` and the
                    // bodies render no control at all rather than a disabled
                    // one. Investors still READ the founder's estimates and
                    // thesis beside the independent assessment — that is the
                    // point of showing both — they simply cannot change them.
                    onEditAssumptions={canEdit ? () => openSetup('assumptions') : undefined}
                    onEditThesis={canEdit ? () => openSetup('thesis') : undefined}
                  />
                )
                : null)}
            </AnalysisShell>
          </>
        )}
      </AnalysisWidthBoundary>
    </ScrollView>
  );

  // OUTSIDE the ScrollView, so it overlays the analysis instead of displacing
  // it. Rendered once and shared by both platform branches below — mounting it
  // inside each would give the web and native trees separate copies of the
  // editors' local state.
  const setupModal = openTarget
    ? (
      <AnalysisSetupModal
        target={openTarget}
        onClose={closeSetup}
        analysisType={tab}
        draft={draft.data?.draft ?? null}
        showContextEditor={showContextEditor}
        blockingFields={blockingFields}
        isSavingContext={saveContext.isPending}
        onSaveContext={saveSetup}
        assumptionsQuery={latestAssumptions}
        isSavingAssumptions={saveAssumptions.isPending}
        onSaveAssumptions={(next) =>
          saveAssumptions.mutate(next, { onSuccess: closeSetup })}
        thesisQuery={latestThesis}
        isSavingThesis={saveThesis.isPending}
        onSaveThesis={(next) =>
          saveThesis.mutate(next, { onSuccess: closeSetup })}
        saveError={saveError}
      />
    )
    : null;

  if (isWeb) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <AppLayout title="Strategic Analysis" breadcrumbs={breadcrumbs}>
          <View style={{ flex: 1 }}>
            {tabs}
            {content}
            {setupModal}
          </View>
        </AppLayout>
        {analysisConsentGate.sheet}
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      {/* headerShown is false on this route, so the safe-area edges are the
          screen's own responsibility — without them the header sits under the
          status bar and the content under the home indicator. */}
      <SafeAreaView
        style={{ flex: 1, backgroundColor: colors.surface.secondary }}
        edges={['top', 'bottom']}
      >
        {/* The company first, then what this screen is about it. A header that
            says only "Strategic Analysis" leaves an investor holding several
            open reports with no way to tell whose is on screen. */}
        <View
          className="flex-row items-center gap-3 px-4 py-3"
          style={{ backgroundColor: colors.surface.primary }}
        >
          {/* An ICON-ONLY button sizes itself. Wrapping one in a fixed box and
              telling the Pressable to `flex: 1` renders the glyph at the wrong
              size in the corner, where `overflow-hidden` clips it to a stroke —
              which is what this button did on device. Text-bearing buttons
              survive that shape because their content sizes the row; an SVG
              does not. Every other back button in the app carries its own
              dimensions, so this one does too. */}
          <AnimatedPressable
            onPress={back}
            accessibilityRole="button"
            accessibilityLabel="Back"
            className="w-10 h-10 rounded-xl items-center justify-center shrink-0"
            hitSlop={{ top: 2, bottom: 2, left: 2, right: 2 }}
            style={{
              backgroundColor: colors.surface.secondary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <ChevronLeft size={20} color={colors.text.primary} />
          </AnimatedPressable>
          <View className="flex-1">
            {displayName ? (
              <Text
                numberOfLines={1}
                className="text-[11px] font-semibold uppercase tracking-widest"
                style={{ color: colors.text.tertiary }}
              >
                {displayName}
              </Text>
            ) : null}
            <Text numberOfLines={1} className="text-lg font-semibold" style={{ color: colors.text.primary }}>
              Strategic analysis
            </Text>
          </View>
        </View>
        {tabs}
        {content}
        {setupModal}
      </SafeAreaView>
      {analysisConsentGate.sheet}
    </>
  );
}

/**
 * Demo bodies.
 *
 * Local constants only. Nothing here reads Supabase, a search provider or a
 * model — the transport already refuses to invoke in mock mode, and this is what
 * the screen renders instead.
 */
function MockBody({
  tab,
  onInspectClaim,
}: {
  tab: AnalysisType;
  onInspectClaim: (claim: ClaimView) => void;
}) {
  const claims = MOCK_CLAIMS[tab];
  switch (tab) {
    case 'competition':
      return (
        <CompetitionAnalysis
          body={MOCK_COMPETITION}
          claims={claims}
          startupName={MOCK_STARTUP_NAME}
          onInspectClaim={onInspectClaim}
        />
      );
    case 'porter':
      return <PorterFiveForces body={MOCK_PORTER} claims={claims} onInspectClaim={onInspectClaim} />;
    case 'pestel':
      return (
        <PestelAnalysis
          body={MOCK_PESTEL}
          claims={claims}
          onInspectClaim={onInspectClaim}
          now={Date.parse('2026-08-13T00:00:00.000Z')}
        />
      );
    case 'market_validation':
      return (
        <MarketValidation
          body={MOCK_MARKET}
          assumptions={MOCK_ASSUMPTIONS}
          claims={claims}
          onInspectClaim={onInspectClaim}
        />
      );
    case 'exit_strategy':
      return (
        <ExitStrategyAnalysis
          body={MOCK_EXIT}
          assessedThesis={MOCK_EXIT_THESIS}
          claims={claims}
          // Demo mode has no startup to save against, so the control is not
          // offered. It used to be wired to a no-op, which rendered an "Edit
          // thesis" button that silently did nothing when pressed.
          onInspectClaim={onInspectClaim}
        />
      );
  }
}

/**
 * "This analysis needs something from you", in one control.
 *
 * The warning colour IS the message, so the button says what pressing it does
 * rather than restating the state — a red button labelled "Missing required
 * information" tells a founder twice that something is wrong and never once
 * what to do. It is a full-width primary action because on the tab where it
 * appears there is nothing else to do.
 */
function SetupReminderButton({ label, onPress }: { label: string; onPress: () => void }) {
  const colors = useThemeColors();
  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{ height: 50, backgroundColor: colors.error }}
    >
      <AnimatedPressable
        onPress={onPress}
        accessibilityRole="button"
        // The detail lives here rather than on screen: a screen reader user gets
        // which analysis is blocked without the sighted layout carrying a panel.
        accessibilityLabel={`Complete analysis inputs for ${label}`}
        className="flex-row items-center justify-center gap-2"
        style={{ flex: 1 }}
      >
        <AlertCircle size={16} color={colors.text.inverse} />
        <Text
          numberOfLines={1}
          className="text-[15px] font-semibold"
          style={{ color: colors.text.inverse }}
        >
          Complete analysis inputs
        </Text>
      </AnimatedPressable>
    </View>
  );
}

/**
 * The demo report for one tab, as a `ReadyReport`.
 *
 * Built through the same discriminated union the real path uses, so the demo
 * cannot render a body the production tree could not — and so a fixture that
 * drifts out of the contract is a compile error rather than a demo that renders
 * something no run could produce.
 */
function mockReadyReport(tab: AnalysisType): ReadyReport {
  switch (tab) {
    case 'competition':
      return { analysisType: 'competition', body: MOCK_COMPETITION };
    case 'porter':
      return { analysisType: 'porter', body: MOCK_PORTER };
    case 'pestel':
      return { analysisType: 'pestel', body: MOCK_PESTEL };
    case 'market_validation':
      return { analysisType: 'market_validation', body: MOCK_MARKET };
    case 'exit_strategy':
      return { analysisType: 'exit_strategy', body: MOCK_EXIT };
  }
}
